// Browser-side executors for the agent's UI tools (@external-adr AGENT-SERVICE-0020-ag-ui-protocol-adoption). The agent's
// NavigateTo/HighlightElement calls arrive as tool-call stream events; the effect happens HERE,
// after validating the untrusted args against our own allowlists (the server has the same
// catalogues, but the browser trusts nothing it didn't verify).

import type { GrowSaveTarget, NewAgentCard } from "@/stores/agent-store";
import { growNoteSelection, type GrowNoteCount } from "./grow-note";
import { parseOrgRecordEntry } from "./org-record-entry";
import { parseStudioProposal, type StudioNotebookContext } from "./studio-proposal";

// Manually synced with router.tsx and the server catalogue (SparkUiTools.kt).
export const ASSISTANT_ROUTES: ReadonlySet<string> = new Set([
  // Keep legacy overview targets accepted for older server tools; the router redirects them.
  "/save",
  "/save/basics",
  "/save/documents",
  "/save/contact",
  "/save/pinboard",
  "/grow",
  "/grow/watch",
  "/grow/play",
  "/grow/map",
  "/match",
  "/share",
  "/extended-search",
  "/settings",
]);

// Manually synced with the data-assistant-anchor attributes and SparkUiTools.kt.
export const ASSISTANT_ANCHORS: ReadonlySet<string> = new Set([
  "save.editVisibility",
  "grow.startQuiz",
  "grow.powerMap",
  "match.create",
  "share.create",
]);

// Match workflow steps (spark StepId). "challenge" is the default and is the bare /match/:id path;
// the other two ride a ?step= param. Synced with SparkUiTools.kt STEPS.
const MATCH_STEPS: ReadonlySet<string> = new Set(["challenge", "people", "result"]);
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export interface UiToolActions {
  navigate: (path: string) => void;
  highlight: (anchor: string) => void;
  // The match the user currently has open (from the /match/:matchId route), or null. Used to resolve
  // OpenMatch calls that omit matchId (= "switch step in the current match").
  currentMatchId?: string | null;
  // Renders a card under the answer. Only a validated payload reaches it.
  showCard?: (card: NewAgentCard) => void;
  // The open Studio notebook, its title and passages: a proposal has to cite passages and may only carry
  // what they and the title state. Undefined outside the Studio.
  studioNotebook?: StudioNotebookContext;
  // What a typed save of the Grow improve answer needs (SaveGrowNote). Undefined where no save can happen.
  growNote?: GrowNoteSaveActions;
}

/**
 * The turn's side of a `SaveGrowNote` call (spark#1218): the save writes at once, so spark acts only on a turn
 * the person typed, with Studio, for an improve answer of this conversation, and once per turn.
 */
export interface GrowNoteSaveActions {
  /** The person typed this turn into the composer: not a chip, not a queued or hidden turn. */
  typed: boolean;
  /** Studio as the save chip requires it: the Save pillar and its Studio item. */
  canSave: boolean;
  /** The latest improve answer's stored save payload in this conversation, or null. */
  latest: () => GrowSaveTarget | null;
  /** Set once the turn's save has started; a second call in the turn then does nothing. */
  turn: { saved: boolean };
  save: (target: GrowSaveTarget, examples: GrowNoteCount[]) => void;
  /** The neutral notice that the result could not be saved from here. */
  unavailable: () => void;
}

// Execute one agent tool call if it is a UI tool with valid args; unknown tools/args are ignored
// with a debug log (same stance as agent-refresh for unmapped tools).
export function executeUiTool(
  name: string,
  args: Record<string, unknown>,
  actions: UiToolActions,
): void {
  if (name === "NavigateTo") {
    const path = args["path"];
    if (typeof path === "string" && ASSISTANT_ROUTES.has(path)) actions.navigate(path);
    else console.debug("[assistant-ui-tools] ignored NavigateTo with invalid path", path);
  } else if (name === "HighlightElement") {
    const anchor = args["anchor"];
    if (typeof anchor === "string" && ASSISTANT_ANCHORS.has(anchor)) actions.highlight(anchor);
    else console.debug("[assistant-ui-tools] ignored HighlightElement with invalid anchor", anchor);
  } else if (name === "OpenMatch") {
    const rawStep = args["step"];
    const step = typeof rawStep === "string" ? rawStep : undefined;
    if (step !== undefined && !MATCH_STEPS.has(step)) {
      console.debug("[assistant-ui-tools] ignored OpenMatch with invalid step", rawStep);
      return;
    }
    const rawId = args["matchId"];
    const matchId = typeof rawId === "string" ? rawId : (actions.currentMatchId ?? undefined);
    if (!matchId || !UUID_RE.test(matchId)) {
      console.debug("[assistant-ui-tools] ignored OpenMatch with no valid matchId", rawId);
      return;
    }
    // "challenge" is the default step and lives at the bare path (matches use-match-editor's setStepId).
    actions.navigate(step && step !== "challenge" ? `/match/${matchId}?step=${step}` : `/match/${matchId}`);
  } else if (name === "ProposeStudioRecords") {
    // Outside the Studio there is no notebook to cite, so nothing can be proposed from it.
    if (!actions.studioNotebook) {
      console.debug("[assistant-ui-tools] ignored ProposeStudioRecords outside the Studio");
      return;
    }
    const proposal = parseStudioProposal(args, actions.studioNotebook);
    if (proposal) actions.showCard?.({ kind: "studioProposal", data: proposal });
    else console.debug("[assistant-ui-tools] ignored ProposeStudioRecords with no usable entry", args);
  } else if (name === "ProposeOrganizationalRecordEntry") {
    const draft = parseOrgRecordEntry(args);
    if (draft) actions.showCard?.({ kind: "orgRecordEntry", data: draft });
    else console.debug("[assistant-ui-tools] ignored ProposeOrganizationalRecordEntry without exactly one subject, a title or a text", args);
  } else if (name === "SaveGrowNote") {
    const grow = actions.growNote;
    // One save per turn; a repeated call neither saves again nor contradicts the save with a notice.
    if (grow?.turn.saved) {
      console.debug("[assistant-ui-tools] ignored a second SaveGrowNote in the same turn");
      return;
    }
    const examples = growNoteSelection(args["examples"]);
    const target = grow?.typed && grow.canSave ? grow.latest() : null;
    if (!grow || !target || !examples) {
      console.debug("[assistant-ui-tools] ignored SaveGrowNote: not a typed turn, no Studio, no improve answer, or invalid examples", args);
      grow?.unavailable();
      return;
    }
    grow.turn.saved = true;
    grow.save(target, examples);
  }
}
